<template>
  <div
    class="scene-element-impulses"
    v-if="visible"
    :data-visible="visible"
    :class="{ 'is-active': impulseIsActive }"
    @pointerdown="handleDownEvent"
    @pointerup="logImpulse"
  >
    <span
      v-if="openCost && openCostValue != 0"
      :class="{ '-not-enough': costsNotMet }"
      class="scene-element-impulses__badge scene-element-impulses__cost"
    >
      {{ $n(openCostValue, "pointsFlexible") }}
      <span class="scene-element-impulses__unit"> {{ item?.unit }} </span>&nbsp;
    </span>

    <span
      v-if="
        effectPlusImpulse &&
        !visualizing &&
        !visibleActiveSources.action &&
        !visibleActiveSources.scheduledEffect
      "
      class="scene-element-impulses__badge scene-element-impulses__effect"
    >
      <effect-expression
        v-slot="{ value: effectValue }"
        :effect-id="effectPlusImpulse.id"
        :expression="effectPlusImpulse.impulseExpressions[0]"
        :parse-context="{
          affectedId: itemId,
          affectedType: itemType,
          targets: netItems?.target,
          resources: netItems?.resource,
          indices: netItems?.index,
          roundIndex: currentRoundIndex
        }"
        :rounds="[{ elementValues }]"
        :sign-required="true"
      >
        {{ effectValue }}
        <span class="scene-element-impulses__unit"> {{ item?.unit }} </span>&nbsp;
      </effect-expression>
    </span>
    
    <template v-else>
      <AnimatedNumber :value="openImpulseValue" :start-value="0" :max-decimal-points="maxDecimalPoints">
        <template #default="{ tweenedValue, digits }">
          <span class="scene-element-impulses__badge" v-if="tweenedValue != 0" :title="formatNumber(openImpulseValue, { maximumFractionDigits: 5 })">
            {{
              formatNumber(tweenedValue, {
                style: "decimal",
                minimumFractionDigits: 0,
                maximumFractionDigits: digits,
                signDisplay: "exceptZero"
              })
            }}
            <span class="scene-element-impulses__unit"> {{ item?.unit }} </span>&nbsp;
          </span>
          <span v-else></span>
        </template>
      </AnimatedNumber>
    </template>
  </div>
</template>

<script setup lang="ts">
import { inject, computed } from "vue";
import { ensureInt } from "@/helpers/utility/Type";
import { toUid } from "@/helpers/utility/Id";
import { formatNumber } from "@/helpers/utility/Formatting";

import AnimatedNumber from "@core/wrappers/AnimatedNumber.vue";
import EffectExpression from "@editor/items/effect/EffectExpression.vue";

import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { addEffects, valueToEffect } from "@editor/items/effect/Effect";
import { useEffectStore } from "../effect/effects.store";
import { useActionStore } from "../action/actions.store";
import { useIncidentStore } from "../incident/incidents.store";
import { useSimulatedRoundStore } from "@editor/views/simulation/simulationVisualizer";

import type { ImpulseItem } from "@editor/items/impulse/ImpulseItem";
import type {
  BaseEffectItem,
  EffectItem
} from "@editor/items/effect/EffectItem";
import {
  keyNetItems,
  keyElementValues
} from "@editor/net/netCanvasInjectionKeys";

const {
  itemType,
  itemId,
  impulse = null,
  effects = [],
  openCost = undefined,
  value = 0,
  extraValueChange = 0,
  maxDecimalPoints = 2
} = defineProps<{
  itemType: "target" | "resource";
  itemId: number;
  impulse?: ImpulseItem | null;
  effects: BaseEffectItem[];
  openCost?: { value: number; actionId: number }[] | undefined;
  value?: number | boolean | null;
  extraValueChange?: number;
  maxDecimalPoints?: number;
}>();

const netItems = inject(keyNetItems);
const elementValues = inject(keyElementValues);
const simulationView = useSimulationViewStore();

const simulation = computed(() => simulationView.simulation);
const scenario = computed(() => simulationView.scenario);
const visualizing = computed(() => simulationView.visualizing);
const selectedRound = computed(() => simulationView.selectedRound);
const currentRoundIndex = computed(() => simulationView.currentRoundIndex);
const effectItems = computed(() => useEffectStore().items);
const actions = computed(() => useActionStore().items);
const incidents = computed(() => useIncidentStore().items);

const item = computed(() => {
  return netItems?.value?.[itemType]?.[itemId];
});

const costsNotMet = computed(() => {
  if (value == null) return false;
  const minValue = item.value?.minValue;
  const maxValue = item.value?.maxValue;
  const val = Number(value);
  if (minValue != null && val + openCostValue.value < minValue) return true;
  if (maxValue != null && val + openCostValue.value > maxValue) return true;
  return false;
});

const visible = computed(() => {
  return (
    openImpulseValue.value != 0 || effects.length > 0 || openCost != null || extraValueChange
  );
});

// === IMPULSES === //
/**
 * returns the impulse source types that should be added up
 */
const activeImpulseSources = computed((): Record<string, boolean> => {
  if (visualizing.value == true) {
    return {
      action: true,
      scheduledEffect: true,
      incident: true,
      relation: true,
      momentum: true,
      flowBranch: true,
      delayed_action: true,
      delayed_incident: true
    };
  }

  if (!selectedRound.value) return {};
  if (!simulation.value) return {};

  if (
    simulation.value.complete ||
    selectedRound.value.index != currentRoundIndex.value
  ) {
    return {
      action: true,
      scheduledEffect: true,
      incident: true,
      relation: true,
      momentum: true,
      flowBranch: true,
      delayed_action: true,
      delayed_incident: true
    };
  }

  return {
    action: false,
    scheduledEffect: false,
    incident: true,
    relation: true,
    momentum: true,
    flowBranch: true,
    delayed_action: true,
    delayed_incident: true,
  };
});

const visibleActiveSources = computed(() => {
  const sources = { ...activeImpulseSources.value };
  if (scenario.value) {
    if (!scenario.value.impulsesFromImpactsVisible) {
      sources.relation = false;
    }
    if (!scenario.value.impulsesFromActionsVisible) {
      sources.action = false;
      sources.delayed_action = false;
    }
    if (!scenario.value.impulsesFromEventsVisible) {
      sources.incident = false;
      sources.delayed_incident = false;
    }
    if (!scenario.value.impulsesFromMomentumsVisible) {
      sources.momentum = false;
    }
    if (!scenario.value.impulsesFromFlowsVisible) {
      sources.flowBranch = false;
    }
  }
  return sources;
});

const openImpulseValue = computed(() => {
  if (simulation.value == null) return 0;
  if (impulse == null) return 0;
  const round = selectedRound.value;
  if (round) {
    // if the round is only partially simulated,
    // add up the parts if the impulse that are active:
    const sources = visibleActiveSources.value;
    const shownValue = impulse.sourceValues.reduce((sum, source) => {
      if (source.effectType == "cost") {
        if (round.played || visualizing.value) return sum + (source.value ?? 0);
        return sum;
      }
      if (
        source.effectType == "effect" &&
        source.effectId != null &&
        typeof source.effectId == "number"
      ) {
        if (scenario.value?.effectsVisibility == "hidden") return sum;
        if (scenario.value?.effectsVisibility == "values-hidden") return sum;
        if (scenario.value?.effectsVisibility != "visible") {
          const effect: EffectItem | undefined =
            effectItems.value?.[source.effectId];
          const activator = getActivator(effect);
          if (activator && activator.effectsVisibility != "as-is") {
            if (activator.effectsVisibility != "visible") return sum;
          }
          if (effect?.visibility != "visible") return sum;
        }
      }
      if (sources?.[source.type] === true) return sum + (source.value ?? 0);
      return sum;
    }, 0);
    return shownValue;
  } else {
    return impulse.sourceValues.reduce(
      (sum, sourceValue) => sum + (sourceValue.value ?? 0),
      0
    );
  }
});

const totalOpenCosts = computed(() => {
  if (openCost == null) return 0;
  return openCost.reduce((sum, openCost) => sum + openCost.value, 0);
});

const openCostValue = computed(() => {
  if (openCost == null) return 0;
  if (simulation.value == null) return 0;
  if (selectedRound.value == null) return 0;
  if (
    simulation.value.complete ||
    selectedRound.value.index != currentRoundIndex.value
  ) {
    return 0;
  }
  const sources = visibleActiveSources.value;
  
  // if the round is simulated,
  // the costs are included in the impulses.
  if (sources.action == true) return 0;

  return totalOpenCosts.value;
});

const impulseIsActive = computed(() => {
  if (impulse == null) return false;
  if (!visualizing.value) return false;
  
  const visualization = useSimulatedRoundStore();
  return visualization.impulseHighlights.has(toUid(itemType, itemId));
});

const extraValueChangeEffect = computed(() => {
  if (extraValueChange == null) return null;
  if (extraValueChange == 0) return null;
  const effect = valueToEffect(extraValueChange, {
    activatorId: -1,
    activatorType: "scheduledEffect",
    affectedType: itemType,
    affectedId: itemId
  });
  return effect;
});

const visibleEffects = computed(() => {
  if (scenario.value?.effectsVisibility == "hidden") return [];
  if (scenario.value?.effectsVisibility == "values-hidden") return [];

  const visibleEffects = effects.filter((effect) => {
    // scenario can force all effects to be visible!
    if (scenario.value?.effectsVisibility == "visible") return true;

    const activator = getActivator(effect);
    if (activator) {
      if (activator.effectsVisibility == "hidden") return false;
      if (activator.effectsVisibility == "visible") return true;
      if (activator.effectsVisibility == "values-hidden") return false;
    }
    if (effect.visibility != "visible") return false;
    return true;
  });

  if (extraValueChangeEffect.value != null) {
    visibleEffects.push(extraValueChangeEffect.value);
  }

  return visibleEffects;
});

const effectPlusImpulse = computed(() => {
  if (visibleEffects.value.length == 0) return null;

  const mergedEffect = visibleEffects.value.reduce((merged, effect) => {
    return addEffects(merged, effect);
  }, valueToEffect(0, {
    activatorId: -1,
    activatorType: "scheduledEffect",
    affectedType: itemType,
    affectedId: itemId
  }));

  if (openImpulseValue.value == 0) {
    return mergedEffect;
  }

  const impulseValueEffect = valueToEffect(openImpulseValue.value, {
    activatorId: -1,
    activatorType: "scheduledEffect",
    affectedType: itemType,
    affectedId: itemId
  });
  const combined = addEffects(impulseValueEffect, mergedEffect);
  return combined;
});

function getActivator(effect: BaseEffectItem | null) {
  if (effect == null) return null;
  const id = effect.activatorId;
  if (id == null) return null;
  if (effect.activatorType == "scheduledEffect") {
    return incidents.value?.[ensureInt(id)];
  }
  if (effect.activatorType == "action") {
    return actions.value?.[ensureInt(id)];
  }
  if (effect.activatorType == "incident") {
    return incidents.value?.[ensureInt(id)];
  }
  return null;
}

function handleDownEvent(
  event: PointerEvent & {
    hasBeenHandled?: boolean;
  }
) {
  event.hasBeenHandled = true;
}

function logImpulse() {
  console.log({ impulse, effects });
}
</script>

<style lang="scss">
.scene-element-impulses {
  position: absolute;
  top: calc(100% - 1px);
  right: -1px;
  left: -1px;
  min-width: 3.75em;
  // min-height: 1.875em;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  border-top: transparent;
  white-space: nowrap;
  gap: 0.5rem;
  pointer-events: none;

  &__badge {
    background-color: var(--color-background-deep);
    border: 1px solid var(--color-border);
    padding-block: 0.25em;
    padding-left: 0.5em;
    font-size: 1em;
    line-height: 1em;
    text-align: right;
    border-radius: 6px;
    pointer-events: all;
  }
  &__effect {
    background-color: var(--color-action-light);
  }
  &__cost {
    background-color: var(--color-background-deep);
    &:where(.-not-enough) {
      background-color: var(--color-error-light);
      color: var(--color-error-dark);
      border-color: var(--color-error);
    }
  }

  &.is-active &__badge {
    background-color: var(--color-action);
    border-color: var(--color-action);
    color: var(--color-action-contrast);
  }

  &__unit {
    font-size: 0.75em;
  }
}
</style>
